// pages/Portfolio.js
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaExternalLinkAlt, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import projects from '../../data/projects';
import './Portfolio.css';

const categories = ['All', ...new Set(projects.map((project) => project.category))];
const PROJECTS_PER_PAGE = 12;

// limit: show only the first N projects with a "View all" link (used on the home page)
// showFilters: category filter buttons and pagination (used on the portfolio page)
const Portfolio = ({ limit, showFilters = false, showHeader = true }) => {
    const [activeCategory, setActiveCategory] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const sectionRef = useRef(null);

    const filteredProjects = activeCategory === 'All'
        ? projects
        : projects.filter((project) => project.category === activeCategory);

    const paginate = showFilters && !limit;
    const totalPages = paginate ? Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE) : 1;
    const visibleProjects = limit
        ? filteredProjects.slice(0, limit)
        : paginate
            ? filteredProjects.slice((currentPage - 1) * PROJECTS_PER_PAGE, currentPage * PROJECTS_PER_PAGE)
            : filteredProjects;

    const changeCategory = (category) => {
        setActiveCategory(category);
        setCurrentPage(1);
    };

    const goToPage = (page) => {
        setCurrentPage(page);
        if (sectionRef.current) {
            const top = sectionRef.current.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <section id="portfolio" className="section portfolio" ref={sectionRef}>
            <div className="container">
                {showHeader && (
                    <div className="section-header">
                        <h2 className="section-title">Our Recent Projects</h2>
                        <p className="section-subtitle">
                            See how we've helped businesses succeed with custom web solutions
                        </p>
                    </div>
                )}

                {showFilters && (
                    <div className="portfolio-filters">
                        {categories.map((category) => (
                            <button
                                key={category}
                                className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
                                onClick={() => changeCategory(category)}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                )}

                <div className="portfolio-grid">
                    {visibleProjects.map(item => (
                        <div key={item.slug} className="portfolio-item card">
                            <div className="portfolio-image">
                                <Link to={`/portfolio/${item.slug}`}>
                                    <img src={item.image} alt={item.title} loading="lazy" />
                                </Link>
                                <div className="portfolio-category">{item.category}</div>
                            </div>

                            <div className="portfolio-content">
                                <h3 className="portfolio-title">
                                    <Link to={`/portfolio/${item.slug}`}>{item.title}</Link>
                                </h3>
                                <p className="portfolio-description">{item.description}</p>

                                <div className="portfolio-tech">
                                    {item.technologies.slice(0, 4).map((tech, index) => (
                                        <span key={index} className="tech-tag">{tech}</span>
                                    ))}
                                </div>

                                <div className="portfolio-actions">
                                    <Link to={`/portfolio/${item.slug}`} className="portfolio-details-link">
                                        View Details <FaArrowRight />
                                    </Link>
                                    {item.liveUrl && (
                                        <a href={item.liveUrl} className="portfolio-live-link" target="_blank" rel="noopener noreferrer">
                                            Live Site <FaExternalLinkAlt />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {totalPages > 1 && (
                    <nav className="pagination" aria-label="Portfolio pages">
                        <button
                            className="page-btn page-arrow"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            <FaChevronLeft />
                        </button>
                        {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                            <button
                                key={page}
                                className={`page-btn ${page === currentPage ? 'active' : ''}`}
                                onClick={() => goToPage(page)}
                                aria-current={page === currentPage ? 'page' : undefined}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className="page-btn page-arrow"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                        >
                            <FaChevronRight />
                        </button>
                    </nav>
                )}

                {limit && filteredProjects.length > limit && (
                    <div className="section-more">
                        <Link to="/portfolio" className="btn btn-outline">
                            View All Projects <FaArrowRight />
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Portfolio;
